import {Component, computed, OnDestroy, OnInit, signal} from '@angular/core';
import {
    IOrderHistoryDateRange,
    IOrderHistoryView,
    IOrderHistoryViewProduct,
    IOrderHistoryViewProductDisplayAttribute
} from '../orders-models';
import {BaseComponent} from '../../base.component';
import {UserService} from '../../common/user.service';
import {LoggerService} from '../../common/logger.service';
import {catchError, forkJoin, from, of, Subject, Subscription, switchMap} from 'rxjs';
import {OrderHistoryCacheService} from "./order-history-cache.service";
import {GqlOrderHistoryService} from "../../../checkout/graphql/order-history/gql-order-history.service";
import {MarkupService} from "../../common/markup.service";
import {BreadcrumbType} from "../../breadcrumb/breadcrumb.models";
import {BreadcrumbService} from "../../breadcrumb/breadcrumb.service";
import {TranslateService} from "../../translation/translate.service";
import {OrderHistoryService} from "./order-history.service";
import {IShopUser} from "../../session/customer-models";

@Component({
    selector: 'order-history',
    templateUrl: './order-history.component.html',
    styleUrls: ['../orders.scss', './order-history.component.scss'],
    standalone: false
})
export class OrderHistoryComponent extends BaseComponent implements OnInit, OnDestroy {

    readonly orderViews = signal<IOrderHistoryView[]>(undefined);
    readonly dateRanges = signal<IOrderHistoryDateRange[]>([]);
    readonly selectedDateRange = signal<IOrderHistoryDateRange>(undefined);
    readonly loading = signal<boolean>(false);
    readonly displayVolume = signal<boolean>(false);
    readonly canManageAutoOrder = signal<boolean>(false);
    readonly canPayPending = signal<boolean>(false);

    // tracking counts walk every packslip and package, so they are derived once per set of orders
    // instead of on every change detection pass
    readonly trackingCounts = computed(() => {
        const counts = new Map<number, number>();
        (this.orderViews() ?? []).forEach((orderView: IOrderHistoryView) =>
            counts.set(orderView.id, this.orderHistoryService.getTrackingCount(orderView)));
        return counts;
    });

    subscription: Subscription = new Subscription();
    private readonly orderViewRequests = new Subject<IOrderHistoryDateRange>();

    readonly headerLabels = [
        'cart.order.history.product.price',
        'cart.order.history.product.volume',
        'cart.order.history.product.quantity'
    ];

    readonly productDisplayAttributes: IOrderHistoryViewProductDisplayAttribute[] = [
        {name: 'totalPrice', isCurrency: true},
        {name: 'totalVolume', isCurrency: false},
        {name: 'quantity', isCurrency: false}
    ];

    constructor(
        private userService: UserService,
        loggerService: LoggerService,
        private markupService: MarkupService,
        private breadcrumbService: BreadcrumbService,
        private translateService: TranslateService,
        private orderHistoryService: OrderHistoryService,
        private orderHistoryCacheService: OrderHistoryCacheService,
        private gqlOrderHistoryService: GqlOrderHistoryService
    ) {
        super(userService, loggerService);
    }

    ngOnInit(): void {
        this.markupService.setTitleFromTranslationTag('cart.order.history');
        this.setBreadcrumb();
        this.listenForOrderViewRequests();
        this.subscription.add(this.userService.getShopUser().subscribe((user: IShopUser) => {
            this.displayVolume.set(user.config.shouldDisplayVolume);
            this.canManageAutoOrder.set(user.config.canAddAutoOrderOnline && user.config.canEditAutoOrderOnline);
            this.canPayPending.set(user.config.canPayPendingOrders);
        }));
        this.init();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    init() {
        const dateRanges: IOrderHistoryDateRange[] = this.orderHistoryCacheService.getDateRanges();
        const selected: IOrderHistoryDateRange = this.orderHistoryCacheService.getSelectedDateRange();
        if (!dateRanges?.length || !selected) {
            this.initDateRanges();
            return;
        }

        this.dateRanges.set(dateRanges);
        this.selectedDateRange.set(selected);
        // only orders loaded for this range come back; anything else means the last fetch never finished
        const cachedOrderViews: IOrderHistoryView[] = this.orderHistoryCacheService.getOrderViewsFor(selected.tag);
        if (cachedOrderViews) {
            this.orderViews.set(cachedOrderViews);
            this.scrollToActiveOrderView();
        } else {
            this.initOrderViews();
        }
    }

    initDateRanges() {
        this.loading.set(true);
        this.subscription.add(this.gqlOrderHistoryService.getDateRanges().subscribe({
            next: (dateRanges: IOrderHistoryDateRange[]) => this.setDateRanges(dateRanges),
            error: (error: any) => {
                this.log.error("Error fetching order history date ranges", error);
                this.loading.set(false);
            }
        }));
    }

    setDateRanges(dateRanges: IOrderHistoryDateRange[]) {
        this.dateRanges.set(dateRanges ?? []);
        if (!dateRanges?.length) {
            this.loading.set(false);
            return;
        }
        // a canceled fetch clears the cached orders but not the choice, so honor a range already selected.
        // matched by tag and taken from this list, since ngModel compares the range by identity
        const cachedTag: string = this.orderHistoryCacheService.getSelectedDateRange()?.tag;
        const selected: IOrderHistoryDateRange = dateRanges.find((range: IOrderHistoryDateRange) => range.tag === cachedTag) ?? dateRanges[0];
        this.selectedDateRange.set(selected);
        this.orderHistoryCacheService.setSelectedDateRange(selected);
        this.initOrderViews();
        this.setDateRangeDisplays(dateRanges);
    }

    // runs alongside the order fetch so the labels never hold up the list
    setDateRangeDisplays(dateRanges: IOrderHistoryDateRange[]) {
        const untranslated: IOrderHistoryDateRange[] = dateRanges.filter((dateRange: IOrderHistoryDateRange) => !dateRange.display);
        if (!untranslated.length) {
            return;
        }

        this.subscription.add(forkJoin(untranslated.map((dateRange: IOrderHistoryDateRange) =>
            from(this.translateService.translate(`cart.order.history.range.${dateRange.tag}`)).pipe(
                catchError((error: any) => {
                    this.log.error("translate date range display", error);
                    return of(dateRange.tag);
                })
            )
        )).subscribe((displays: string[]) => {
            untranslated.forEach((dateRange: IOrderHistoryDateRange, index: number) => dateRange.display = displays[index]);
            // re-emit the same objects so the selected range keeps the identity ngModel compares on
            this.dateRanges.set([...dateRanges]);
        }));
    }

    // switchMap so selecting a new range cancels the request for the old one. without it a late
    // response could overwrite, or a late failure could clear, the range actually on screen
    listenForOrderViewRequests() {
        this.subscription.add(this.orderViewRequests.pipe(
            switchMap((dateRange: IOrderHistoryDateRange) => this.gqlOrderHistoryService.getOrderViews(dateRange).pipe(
                catchError((error: any) => {
                    this.log.error("Error fetching order history", error);
                    return of(null);
                })
            ))
        ).subscribe((orderViews: IOrderHistoryView[]) => {
            if (orderViews) {
                this.orderViews.set(orderViews);
                this.scrollToActiveOrderView();
            } else {
                // the cache was already invalidated when this fetch started, so only the screen needs clearing
                this.orderViews.set([]);
            }
            this.loading.set(false);
        }));
    }

    initOrderViews() {
        this.loading.set(true);
        // starting a fetch supersedes whatever is cached for this range, so drop it now rather than on
        // completion. a fetch canceled by navigating away never reaches the handler below, and would
        // otherwise leave the previous result cached to be shown again on return
        this.orderHistoryCacheService.clearOrderViewsFor(this.selectedDateRange()?.tag);
        this.orderViewRequests.next(this.selectedDateRange());
    }

    setBreadcrumb() {
        this.breadcrumbService.setBreadcrumb(null, BreadcrumbType.ORDER_HISTORY);
    }

    scrollToActiveOrderView() {
        const activeOrderView: IOrderHistoryView = this.orderHistoryCacheService.getActiveOrderView();
        if (activeOrderView) {
            // wait for the next event loop tick to ensure DOM is rendered
            setTimeout(() => {
                const element: HTMLElement = document.getElementById(`order${activeOrderView.id}`);
                if (element) {
                    element.scrollIntoView();
                }
            }, 0);
        }
    }

    onDateRangeChange(dateRange: IOrderHistoryDateRange) {
        this.selectedDateRange.set(dateRange);
        this.orderHistoryCacheService.setSelectedDateRange(dateRange);
        this.initOrderViews();
    }

    setActiveOrderView(orderView: IOrderHistoryView) {
        this.orderHistoryCacheService.setActiveOrderView(orderView);
    }

    addItemToCart(product: IOrderHistoryViewProduct, orderId: number) {
        this.orderHistoryCacheService.addProductAdding(product, orderId);
        this.orderHistoryService.addItemToCart(product, orderId);
    }

    addFullOrderToCart(products: IOrderHistoryViewProduct[], orderId: number) {
        this.orderHistoryCacheService.addAllProductsAdding(orderId);
        this.orderHistoryService.addFullOrderToCart(products, orderId);
    }

    viewTracking(orderView: IOrderHistoryView) {
        this.orderHistoryService.openTrackingModal(orderView);
    }

    managePayment(orderView: IOrderHistoryView) {
        // refresh the order list when a payment completes so the order's status/payments update
        this.orderHistoryService.openManagePaymentModal(orderView, () => this.initOrderViews());
    }
}
